import { useState, useEffect } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import moment from "moment";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import {
  useAddStudentMutation,
  useGetStudentDetailByNumberQuery,
} from "src/services/StudentServices";
import { showToast } from "src/utils/showToaster";
import { object, string } from "yup";
import StudentForm from "../Layouts/StudentForm";
import { useNavigate } from "react-router-dom";
import useCountries from "src/hooks/useCountries";
import useCountryStates from "src/hooks/useCountryStates";
import useStateCities from "src/hooks/useStateCities";
import { StudentFormValues } from "src/models/Student.model";
import { mobileRegExp } from "src/utils/validations/indianPhoneRegex";

const AddStudentFormWrapper = () => {
  const navigate = useNavigate();
  const [toRedirectNext, setToRedirectNext] = useState(false);

  const [defaultCountryId, setDefaultCountryId] = useState<any[]>([]);
  const [defaultStateId, setDefaultStateId] = useState<any[]>([]);
  const [defaultCityId, setDefaultCityId] = useState<any[]>([]);
  const [studentNumber, setStudentNumber] = useState<any>();
  const [studentData, setStudentData] = useState<any>({});

  //STATE
  const [addStudent] = useAddStudentMutation();

  // Step 1: Remove the country code if present
  const withoutCountryCode = studentNumber?.replace(/^\+91\s?/, "");

  // Step 2: Remove all spaces
  const finalNumber = withoutCountryCode?.replace(/\s/g, "");

  const { data, isLoading, isFetching } = useGetStudentDetailByNumberQuery(
    finalNumber,
    { skip: !finalNumber }
  );

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setStudentData(data?.data?.[0]);
    }
  }, [data, isLoading, isFetching]);

  // Default countrie
  const { countries, isDataLoading: isCountriesLoading } = useCountries();
  useEffect(() => {
    if (!isCountriesLoading) {
      setDefaultCountryId(countries?.filter((el) => el?.isDefault) || []);
    }
  }, [isCountriesLoading, countries]);

  // Default State
  const { countryStates, isDataLoading: isCountryStatesLoading } =
    useCountryStates(defaultCountryId?.[0]?._id || "");

  useEffect(() => {
    if (!isCountryStatesLoading) {
      setDefaultStateId(countryStates?.filter((el) => el?.isDefault) || []);
    }
  }, [isCountryStatesLoading, countryStates]);

  // Default City

  const { stateCities, isDataLoading: isStateCitiesLoading } = useStateCities(
    defaultStateId?.[0]?._id
  );

  useEffect(() => {
    if (!isStateCitiesLoading) {
      setDefaultCityId(stateCities?.filter((el) => el?.isDefault) || []);
    }
  }, [isStateCitiesLoading, stateCities]);

  // Form Initial Values
  const initialValues: StudentFormValues = {
    name: studentData?.name || "",
    mobile: studentData?.mobile || "",
    email: studentData?.email || "",
    password: '' ,
    username: '' ,
    blood_group: "",
    address: "",
    emergency_contact_no: "",
    branchId:
      studentData?.branchId?.map((branch: any) => {
        return {
          label: branch?.branchName,
          value: branch?._id,
        };
      }) || [],
    instagram_id: studentData?.instagram_id || "",
    facebook_id: studentData?.facebook_id || "",
    youtube_id: studentData?.youtube_id || "",
    twitter_id: studentData?.twitter_id || "",
    linkedin_id: studentData?.linkedin_id || "",
    dob: studentData?.dob || "",
    marital_status: studentData?.marital_status || "",
    gender: studentData?.gender || "",
    academic_history: studentData?.academic_history?.map((el: any) => {
      return {
        collge_school_name: el?.collge_school_name,
        course_name: el?.course_name,
        course_type: el?.course_type,
        collge_school_tier: el?.collge_school_tier,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    work_history: studentData?.work_history?.map((el: any) => {
      return {
        company_name: el?.company_name,
        job_title: el?.job_title,
        job_description: el?.job_description,
        start_year: el?.start_year,
        end_year: el?.end_year,
        currently_pursuing: el?.currently_pursuing,
      };
    }),
    whatsApp_mobile: studentData?.whatsApp_mobile || "",
    countryId: studentData?.cityId
      ? {
          label: studentData?.countryLabel,
          value: studentData?.countryId,
        }
      : defaultCountryId?.map((el: any) => {
          return {
            label: el?.countryName,
            value: el?._id,
          };
        })[0],
    stateId: studentData?.cityId
      ? {
          label: studentData?.stateLabel,
          value: studentData?.stateId,
        }
      : defaultStateId?.map((el: any) => {
          return {
            label: el?.stateName,
            value: el?._id,
          };
        })[0],
    cityId: studentData?.cityId
      ? {
          label: studentData?.cityLabel,
          value: studentData?.cityId,
        }
      : defaultCityId?.map((el: any) => {
          return {
            label: el?.cityName,
            value: el?._id,
          };
        })[0],
  };

  // Validation Schema
  const validationSchema = object().shape({ 
    password : string().required("Please enter password")  ,
    mobile: string()
      .trim()
      .matches(mobileRegExp, "mobile number is not valid")
      .required("Please enter mobile number"),
    whatsApp_mobile: string()
      .trim()
      .matches(mobileRegExp, "mobile number is not valid"),
    emergency_contact_no: string()
      .trim()
      .matches(mobileRegExp, "mobile number is not valid"),
    name: string().required("Please enter name"),
    stateId: object().required("please select state"),
    cityId: object().required("please select city"),
    email:string().email().required('email is required')
  });

  // Handle Submit
  const handleSubmit = (
    values: StudentFormValues,
    { setSubmitting, resetForm }: FormikHelpers<StudentFormValues>
  ) => {
    const formattedValues = {
      ...values,
      mobile: finalNumber,
      dob: values?.dob ? moment(values?.dob).format("YYYY-MM-DD") : "",
      branchId: values?.branchId?.map((branch) => branch?.value),
      password : values?.password ,
      work_history: values?.work_history?.map((data: any) => {
        return {
          ...data,
          start_year: data?.start_year
            ? moment(data?.start_year).year()?.toString()
            : "",
          end_year: data?.end_year
            ? moment(data?.end_year).year()?.toString()
            : "",
        };
      }),
      academic_history: values?.academic_history?.map((data: any) => {
        return {
          ...data,
          start_year: data?.start_year
            ? moment(data?.start_year).year()?.toString()
            : "",
          end_year: data?.end_year
            ? moment(data?.end_year).year()?.toString()
            : "",
        };
      }),
      countryId: values?.countryId?.value,
      stateId: values?.stateId?.value,
      cityId: values?.cityId?.value,
    };
    addStudent(formattedValues).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          toRedirectNext
            ? navigate(
                `/student/edit/${res?.data?.data?._id}/personal-information`
              )
            : navigate("/student");
          resetForm();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <SideNavLayout>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <StudentForm
              setStudentNumber={setStudentNumber}
              onRedirect={() => setToRedirectNext(true)}
              formikProps={formikProps}
            />
          </Form>
        )}
      </Formik>
    </SideNavLayout>
  );
};

export default AddStudentFormWrapper;
